"use client";

import { createContext, useContext, useMemo } from "react";

export type WirePaymentMethodConfig = {
  id: "wire";
  name: string;
  enabled: boolean;
  discountActive: boolean;
  discountPercent: number;
  instructions: string[];
  instructionsText: string;
};

export type MercadoPagoPaymentMethodConfig = {
  id: "mercadopago";
  name: string;
  enabled: boolean;
  configured: boolean;
  publicKey?: string | null;
  integratorId?: string | null;
  testMode?: boolean;
};

export type StripePaymentMethodConfig = {
  id: "stripe";
  name: string;
  enabled: boolean;
  publishableKey?: string | null;
  currency?: string | null;
};

export type DlocalPaymentMethodConfig = {
  id: "dlocal";
  name: string;
  enabled: boolean;
};

export type PaymentMethodConfig =
  | WirePaymentMethodConfig
  | MercadoPagoPaymentMethodConfig
  | StripePaymentMethodConfig
  | DlocalPaymentMethodConfig;

export type PaymentMethodsContextValue = {
  methods: PaymentMethodConfig[];
  wire?: WirePaymentMethodConfig;
  mercadoPago?: MercadoPagoPaymentMethodConfig;
  stripe?: StripePaymentMethodConfig;
  dlocal?: DlocalPaymentMethodConfig;
};

const defaultValue: PaymentMethodsContextValue = { methods: [] };

const PaymentMethodsContext = createContext<PaymentMethodsContextValue>(defaultValue);

export function PaymentMethodsProvider({
  initialValue,
  children,
}: {
  initialValue: PaymentMethodsContextValue;
  children: React.ReactNode;
}) {
  const memoized = useMemo(() => {
    let next = initialValue;
    if (!next.stripe) {
      const stripeMethod = next.methods.find((method) => method.id === "stripe") as
        | StripePaymentMethodConfig
        | undefined;
      if (stripeMethod) {
        next = { ...next, stripe: stripeMethod };
      }
    }
    if (!next.wire) {
      const wireMethod = next.methods.find((method) => method.id === "wire") as
        | WirePaymentMethodConfig
        | undefined;
      if (wireMethod) {
        next = { ...next, wire: wireMethod };
      }
    }
    if (!next.mercadoPago) {
      const mpMethod = next.methods.find((method) => method.id === "mercadopago") as
        | MercadoPagoPaymentMethodConfig
        | undefined;
      if (mpMethod) {
        next = { ...next, mercadoPago: mpMethod };
      }
    }
    if (!next.dlocal) {
      const dlocalMethod = next.methods.find((method) => method.id === "dlocal") as
        | DlocalPaymentMethodConfig
        | undefined;
      if (dlocalMethod) {
        next = { ...next, dlocal: dlocalMethod };
      }
    }
    return next;
  }, [initialValue]);

  return <PaymentMethodsContext.Provider value={memoized}>{children}</PaymentMethodsContext.Provider>;
}

export function usePaymentMethods() {
  return useContext(PaymentMethodsContext);
}
